/**
 * 错误处理服务
 * 提供应用全局统一的错误处理机制
 *
 * 核心功能：
 * 1. 创建标准化的错误对象
 * 2. 处理不同类型的错误
 * 3. 记录错误日志
 * 4. 为用户提供友好的错误提示
 * 5. 支持错误上报（为后续云存储做准备）
 */
// 从types包导入错误相关类型
import { ErrorType, ErrorSeverity } from '@readsavor/types';
/**
 * 错误处理服务类
 * 单例模式实现
 */
export class ErrorHandlerService {
    /**
     * 私有构造函数，防止直接实例化
     */
    constructor() {
        // 是否已初始化
        this.initialized = false;
        // 日志记录器
        this.logger = null;
    }
    /**
     * 获取服务实例
     */
    static getInstance() {
        if (!ErrorHandlerService.instance) {
            ErrorHandlerService.instance = new ErrorHandlerService();
        }
        return ErrorHandlerService.instance;
    }
    /**
     * 设置日志记录器
     */
    setLogger(logger) {
        this.logger = logger;
    }
    /**
     * 初始化错误处理服务
     */
    initialize(globalErrorCallback) {
        if (this.initialized) {
            this.logger?.warn('ErrorHandlerService has already been initialized', { tag: 'ERROR' });
            return;
        }
        this.globalErrorCallback = globalErrorCallback;
        this.initialized = true;
        // 设置全局未捕获错误处理器（为后续扩展准备）
        window.addEventListener('unhandledrejection', event => {
            this.handleError(event.reason, {
                severity: ErrorSeverity.HIGH,
                component: 'Global',
                operation: 'Promise',
                userMessage: 'Operation failed, please try again later',
            });
        });
        this.logger?.info('ErrorHandlerService initialized successfully', { tag: 'ERROR' });
    }
    /**
     * 创建标准化的错误对象
     */
    createError(originalError, type = ErrorType.UNEXPECTED, options = {}) {
        // 处理原始错误消息
        const message = originalError instanceof Error
            ? originalError.message
            : typeof originalError === 'string'
                ? originalError
                : 'Unknown error';
        // 创建错误代码
        const code = `${type}_${Date.now().toString(36)}`;
        // 创建元数据
        const metadata = {
            timestamp: Date.now(),
            component: options.component,
            operation: options.operation,
            data: options.data,
            stack: originalError instanceof Error ? originalError.stack : undefined,
        };
        // 转换originalError为AppError接口要求的类型
        const safeOriginalError = originalError instanceof Error ||
            typeof originalError === 'string' ||
            (originalError !== null && typeof originalError === 'object')
            ? originalError
            : String(originalError);
        // 创建AppError对象
        return {
            type,
            code,
            message,
            originalError: safeOriginalError,
            severity: options.severity || ErrorSeverity.MEDIUM,
            metadata,
            userMessage: options.userMessage,
            suggestions: options.suggestions,
            reported: false,
        };
    }
    /**
     * 处理错误
     * 核心错误处理方法
     */
    handleError(error, options = {}) {
        // 确定错误类型
        let errorType = options.type || ErrorType.UNEXPECTED;
        // 尝试根据错误特征确定错误类型
        if (!options.type && error instanceof Error) {
            if (error.name === 'NetworkError' || error.message.includes('network')) {
                errorType = ErrorType.NETWORK;
            }
            else if (error.message.includes('timeout')) {
                errorType = ErrorType.TIMEOUT;
            }
            else if (error.message.includes('database') || error.message.includes('storage')) {
                errorType = ErrorType.DATABASE;
            }
            else if (error.message.includes('translation')) {
                errorType = ErrorType.TRANSLATION;
            }
            else if (error.message.includes('document')) {
                errorType = ErrorType.DOCUMENT;
            }
        }
        // 创建标准错误对象
        const appError = this.createError(error, errorType, options);
        // 记录错误日志
        this.logError(appError);
        // 如果设置了全局回调，调用它
        if (this.globalErrorCallback && !options.silent) {
            this.globalErrorCallback(appError);
        }
        // 如果需要，重新抛出错误
        if (options.rethrow) {
            throw error;
        }
        return appError;
    }
    /**
     * 记录错误日志
     */
    logError(error) {
        const logData = {
            code: error.code,
            type: error.type,
            severity: error.severity,
            component: error.metadata?.component,
            operation: error.metadata?.operation,
            timestamp: error.metadata?.timestamp,
        };
        // 根据严重性选择日志级别
        switch (error.severity) {
            case ErrorSeverity.LOW:
                this.logger?.info(`[Error] ${error.message}`, { tag: 'ERROR', data: logData });
                break;
            case ErrorSeverity.MEDIUM:
                this.logger?.warn(`[Error] ${error.message}`, { tag: 'ERROR', data: logData });
                break;
            case ErrorSeverity.HIGH:
            case ErrorSeverity.CRITICAL:
            default:
                this.logger?.error(`[Error] ${error.message}`, {
                    error: error.originalError,
                    tag: 'ERROR',
                    data: logData,
                });
                break;
        }
    }
    /**
     * API错误处理方法
     * 专门处理API调用中的错误
     */
    handleApiError(error, operation, options = {}) {
        return this.handleError(error, {
            ...options,
            type: ErrorType.API,
            component: options.component || 'API',
            operation,
        });
    }
    /**
     * 翻译错误处理方法
     * 处理翻译服务中的错误
     */
    handleTranslationError(error, operation, options = {}) {
        return this.handleError(error, {
            ...options,
            type: ErrorType.TRANSLATION,
            component: options.component || 'Translation',
            operation,
        });
    }
    /**
     * 文档错误处理方法
     * 处理文档相关的错误
     */
    handleDocumentError(error, operation, options = {}) {
        return this.handleError(error, {
            ...options,
            type: ErrorType.DOCUMENT,
            component: options.component || 'Document',
            operation,
        });
    }
}
//# sourceMappingURL=error-handler.service.js.map